import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import useErrors from "../../../libs/useErrors";

import { KeyValue } from "../../../types/keyValue";

import { useUserContext } from "../../../contexts/userContext";

import {
  BuildingFloorItem,
  getBuildingFloorList,
} from "../../../services/buildingServices";
import {
  GoodsServiceItem,
  GoodsServiceItemRequest,
  GoodsServiceRequest,
  GoodsServiceRequestType,
  createGoodsServiceRequest,
  getGoodsServiceItems,
} from "../../../services/goodsRequestServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";

import CommonSelect from "../../../components/Select";
import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonLabel from "../../../components/Label";
import CommonInput from "../../../components/Input/CommonInput";
import { AuthHeader } from "../../../components/Layout/Auth/Header";
import HorizontalRule from "../../../components/Rule/Horizontal";
import RadioButtonGroup from "../../../components/Input/RadioButtonGroup";
import BottomButtonGroup from "../../../components/Button/Bottom";
import FindBuildingPopup from "../../../components/Popup/FindBuilding";
import CommonPopup from "../../../components/Popup";
import BottomGroup from "../../../components/Button/Group";
import CommonButton from "../../../components/Button";
import SimplePopup from "../../../components/Popup/Simple";
import ErrorMessage from "../../../components/Message/Error";

import { TalkFindBuildingDownArrow } from "../../Talk";
import { Reason } from "../../../types/location";

type Props = {};

const PRD_CUSTOM_BUILD_ID = "BD_0010089"  // k 스퀘어
const DEV_CUSTOM_BUILD_ID = "BD_0000179"

const createRequestTypeItemOption = (buildingId: string| undefined ) => {
  const RequestTypeItemOption = [
    { key: "REFILL", value: "OA실" },
    { key: "PICK_UP", value: "방문수령" },
  ];

  if (isCheckCustomBuilding(buildingId)) {
    delete RequestTypeItemOption[1];
  }

  return  RequestTypeItemOption;
}

const isCheckCustomBuilding = (buildingId: string| undefined ) => {
  if (process.env.REACT_APP_ENV === "development" )
    return DEV_CUSTOM_BUILD_ID === buildingId;
  return PRD_CUSTOM_BUILD_ID === buildingId;
}

export default function GoodsRequest(props: Props) {
  const buildingSelectRef = useRef<HTMLDivElement>(null);
  const requestTypeRef = useRef<HTMLDivElement>(null);
  const usedDepartmentRef = useRef<HTMLInputElement>(null);
  const goodsRef = useRef<React.RefObject<HTMLInputElement>[]>([]);
  const extraRequestRef = useRef<HTMLInputElement>(null);
  const selectFloorRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [popup, setPopup] = useState<string>("requestType");
  const [user] = useUserContext();
  const navigate = useNavigate();
  const [floorList, setFloorList] = useState<BuildingFloorItem[]>([]);
  const [selectedFloor, setSelectedFloor] = useState<
    BuildingFloorItem | undefined
  >(user.floor);
  const [errors, { removeError, setError, clearErrors }] = useErrors();
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const reason = Reason.BUILDING_CHANGE;

  const [buildingInfo, setBuildingInfo] = useState({
    buildingId: user.buildingId,
    buildingName: user.buildingName,
  });

  const selectedExtraRequestItemOption = [
    { key: "false", value: "괜찮아요" },
    { key: "true", value: "다른 요청사항이 있어요" },
  ];

  const selectedRequestTypeItemOption = useMemo( () => {
    return createRequestTypeItemOption(buildingInfo.buildingId);
  }, [buildingInfo.buildingId])

  const [selectedExtraRequestItem, setSelectedExtraRequestItem] =
    useState<KeyValue>(selectedExtraRequestItemOption[0]);
    
  const [selectedRequestTypeItem, setSelectedRequestTypeItem] =
    useState<KeyValue>(selectedRequestTypeItemOption[0]);

  const [goodsServiceRequest, setGoodsServiceRequest] = useState<
    Partial<GoodsServiceRequest>
  >({});

  const [goodsServiceItemList, setGoodsServiceItemList] = useState<
    GoodsServiceItem[]
  >([]);

  const loadGoodsServiceItem = async () => {
    setIsLoading(true);
    if (user.companyId === undefined) return;
    try {
      const result = await getGoodsServiceItems(
        goodsServiceRequest?.companyId ?? user.companyId,
        goodsServiceRequest?.requestType ?? "REFILL"
      );

      if (result.status === HttpStatusCode.Ok) {
        setGoodsServiceItemList(result.data);
        setGoodsServiceRequest((prev) => ({
          ...prev,
          companyId: user.companyId,
          buildingId: buildingInfo.buildingId,
          usedDepartment: undefined,
          extraRequest: undefined,
          goodsServiceItemList: result.data.map((item) => ({
            goodsId: item.goodsId,
            count: 0,
          })),
        }));

        if (result.data.length > 0) {
          goodsRef.current = Array(result.data.length)
            .fill(0)
            .map(
              (_, i) =>
                goodsRef.current[i] ?? React.createRef<HTMLInputElement>()
            );
        }
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("신청 없습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const [goodsServiceId, setGoodsServiceId] = useState<string>("");

  const getGoodsName = (goodsId: string) => {
    const goods = goodsServiceItemList?.find(
      (item) => item.goodsId === goodsId
    );
    return goods?.goodsName;
  };

  const validationRequestType = (error?: boolean) => {
    if (selectedRequestTypeItem.key === "REFILL") {
      if (selectedFloor === undefined) {
        if (error) setError("selectFloor", "층수를 선택해주세요.");
        return false;
      }
    }
    return true;
  };

  const handleConfirmRequestType = () => {
    if (!validationRequestType(true)) return;

    setGoodsServiceRequest((prev) => ({
      ...prev,
      requestType: selectedRequestTypeItem.key as GoodsServiceRequestType,
      requestBuildingFloorId: selectedFloor?.buildingFloorId,
    }));

    setPopup("");
  };

  const validation = (error?: boolean) => {
    if (buildingInfo.buildingId === undefined) {
      if (error) {
        setError("building", "건물을 선택해주세요.");
        buildingSelectRef.current?.focus();
      }
      return false;
    }

    if (goodsServiceRequest.requestType === undefined) {
      if (error) {
        setError("requestType", "상세 위치를 선택해주세요.");
        requestTypeRef.current?.focus();
      }
      return false;
    }

    if (goodsServiceRequest.requestType === "REFILL") {
      //OA실
      if (
        selectedFloor === undefined ||
        goodsServiceRequest.requestBuildingFloorId === undefined
      ) {
        if (error) {
          setError("requestType", "상세 위치를 선택해주세요.");
          requestTypeRef.current?.focus();

          setError("selectFloor", "층수를 선택해주세요.");
          selectFloorRef.current?.focus();
        }
        return false;
      }
    }

    if (
      goodsServiceRequest.usedDepartment === undefined ||
      goodsServiceRequest.usedDepartment === ""
    ) {
      if (error) {
        setError("usedDepartment", "사용 부서명을 입력해주세요.");
        usedDepartmentRef.current?.focus();
      }
      return false;
    }

    if (goodsServiceRequest.goodsServiceItemList !== undefined) {
      for (
        let i = 0;
        i < goodsServiceRequest.goodsServiceItemList.length;
        i++
      ) {
        if (
          goodsServiceRequest.goodsServiceItemList[i].count === undefined ||
          goodsServiceRequest.goodsServiceItemList[i].count < 0 ||
          isNaN(goodsServiceRequest.goodsServiceItemList[i].count)
        ) {
          if (error) {
            setError("goods_" + i, "알맞은 신청 개수를 입력해주세요.");
            goodsRef.current[i].current?.focus();
          }
          return false;
        }
      }

      if (goodsServiceRequest.goodsServiceItemList.length > 0) {
        const itemList: GoodsServiceItemRequest[] =
          goodsServiceRequest.goodsServiceItemList.filter(
            (item) => item.count !== 0
          );

        if (itemList.length === 0) {
          if (error) {
            setError(
              "goods_count",
              "요청 품목이 적어도 하나는 0개 보다 많아야 합니다."
            );
            setError("goods_0", "알맞은 신청 개수를 입력해주세요.");
            goodsRef.current[0].current?.focus();
          }
          return false;
        }
      } else {
        if (error) setError("goods_count", "신청 가능한 요청 품목이 없습니다.");
        return false;
      }

      if (selectedExtraRequestItem.key === "true") {
        if (
          goodsServiceRequest.extraRequest === undefined ||
          goodsServiceRequest.extraRequest === ""
        ) {
          if (error) {
            setError("extraRequest", "요청 사항을 입력해주세요.");
            extraRequestRef.current?.focus();
          }
          return false;
        }
      }
    }

    return true;
  };

  const handleConfirm = () => {
    if (!validation(true)) return;
    setPopup("confirm");
    //clearErrors();
  };

  const handleConfirmApply = async () => {
    setIsLoading(true);

    const itemList: GoodsServiceItemRequest[] =
      goodsServiceRequest.goodsServiceItemList?.filter(
        (item) => item.count !== 0
      ) ?? [];

    try {
      const result = await createGoodsServiceRequest({
        ...goodsServiceRequest,
        goodsServiceItemList: itemList,
      } as GoodsServiceRequest);

      if (result.status === HttpStatusCode.Ok) {
        setGoodsServiceId(result.data.goodsServiceId);
        setPopup("success");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("신청에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user.companyId) loadGoodsServiceItem();
  }, [goodsServiceRequest.requestType]);

  useEffect(() => {
    setBuildingInfo({
      buildingId: user.buildingId,
      buildingName: user.buildingName,
    });
    if (user.buildingId && user.companyId) loadGoodsServiceItem();
  }, [user.buildingId, user.buildingName, user.companyId]);

  useEffect(() => {
    if (buildingInfo.buildingId) {
      setIsLoading(true);
      getBuildingFloorList({
        buildingId: buildingInfo.buildingId,
      })
        .then((res) => {
          if (res.status === HttpStatusCode.Ok) {
            setFloorList(res.data);
            const selectedFloor = res.data.find(
              (f) => f.buildingFloorId === ""
            );
            setSelectedFloor(selectedFloor);
            setSelectedRequestTypeItem(selectedRequestTypeItemOption[0]);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
      setGoodsServiceRequest((prev) => ({
        ...prev,
        buildingId: buildingInfo.buildingId,
        requestType: undefined,
        requestBuildingFloorId: undefined,
      }));
      setPopup("requestType");
      clearErrors();
    }
  }, [buildingInfo.buildingId, buildingInfo.buildingName]);

  return (
    <>
      {loadingComponent}
      <div className="px-5">
        <StickyHeader
          title="사무용품 신청"
          onBackClick={() => navigate("/view")}
        />
        <AuthHeader className="flex flex-col gap-2.5 leading-[38px] tracking-[-1.2px]">
          <div
            className="flex w-fit items-center"
            tabIndex={-1}
            ref={buildingSelectRef}
            onClick={() => {
              removeError("building");
              setPopup("selectBuilding");
            }}
          >
            {buildingInfo?.buildingName === undefined ? (
              <div
                className={clsx([
                  "flex flex-row",
                  { "text-gray-400": !errors["building"] },
                  {
                    "text-brand-primary-magenta opacity-80": errors["building"],
                  },
                ])}
              >
                건물을 선택해주세요
              </div>
            ) : (
              <div className="flex w-fit items-center border-b-2 border-black gap-2">
                <div className="font-medium truncate max-w-[calc(100vw-70px)]">
                  {buildingInfo?.buildingName}
                </div>{" "}
                <TalkFindBuildingDownArrow />
              </div>
            )}
          </div>

          <div
            className="flex flex-wrap"
            tabIndex={-1}
            ref={requestTypeRef}
            onClick={() => {
              removeError("requestType");
              removeError("selectFloor");
              if (
                goodsServiceRequest.requestType === undefined ||
                goodsServiceRequest.requestType === "PICK_UP"
              ) {
                const selectedFloor = floorList.find(
                  (f) => f.buildingFloorId === ""
                );
                setSelectedFloor(selectedFloor);
              }
              setPopup("requestType");
            }}
          >
            {goodsServiceRequest.requestType === undefined ||
            (goodsServiceRequest.requestType === "REFILL" &&
              (!goodsServiceRequest.requestBuildingFloorId ||
                selectedFloor === undefined)) ? (
              <div
                className={clsx([
                  "flex flex-row",
                  { "text-gray-400": !errors["requestType"] },
                  {
                    "text-brand-primary-magenta opacity-80":
                      errors["requestType"],
                  },
                ])}
              >
                상세 위치를 선택해주세요
              </div>
            ) : (
              <>
                {goodsServiceRequest.requestType === "REFILL" ? (
                  <div className="flex flex-row">
                    <div className="font-medium border-b-2 border-black">
                      {selectedFloor?.floorName} OA실
                    </div>
                    &nbsp;로
                  </div>
                ) : (
                  <div className="flex flex-row">
                    <div className="font-medium border-b-2 border-black ">
                      방문수령
                    </div>
                    &nbsp;으로
                  </div>
                )}
              </>
            )}
          </div>
          <div className="">제공 해드릴게요.</div>
        </AuthHeader>

        <div className="flex flex-col pt-[30px] gap-5 pb-5">
          <div className="flex flex-col gap-2.5">
            <CommonLabel label="사용 부서" required />
            <CommonInput
              placeholder="사용 부서명을 입력해주세요."
              maxLength={100}
              onChange={(value) => {
                removeError("usedDepartment");
                setGoodsServiceRequest((prev) => ({
                  ...prev,
                  usedDepartment: value,
                }));
              }}
              value={goodsServiceRequest.usedDepartment ?? ""}
              ref={usedDepartmentRef}
              error={errors["usedDepartment"]}
            />
          </div>

          {goodsServiceRequest.requestType !== undefined && (
            <div className="flex flex-col gap-2.5">
              <div>
              <CommonLabel label="요청 품목" required />
              {
                isCheckCustomBuilding(buildingInfo.buildingId) && 
                  <div className="mb-1 text-xs font-medium  text-brand-label-text text-brand-neutral-40">
                      ※OA실 상세 위치를 기타 요청사항에 기재해 주세요.
                </div>
              }
              </div>
              <div className="flex flex-col p-2.5 bg-[#F3F3F3]">
                <div className="flex flex-col">
                  {goodsServiceItemList && goodsServiceItemList.length > 0 ? (
                    goodsServiceItemList?.map((goods, index) => (
                      <div key={goods.goodsId}>
                        <div className="flex flex-row justify-between items-center w-full">
                          <div className="flex flex-col text-base font-medium leading-[17px] w-fit max-w-[50vw]">
                            <div className="truncate">{goods.goodsName}</div>
                          </div>
                          <div className="flex flex-col">
                            <CommonInput
                              type="number"
                              pattern="[0-9]*"
                              min={1}
                              max={999}
                              maxLength={3}
                              defaultValue={""}
                              wrapperClassName="w-[130px]"
                              placeholder="신청 개수 입력"
                              onChange={(value) => {
                                goodsServiceRequest.goodsServiceItemList?.forEach(
                                  (v, i) => {
                                    removeError("goods_" + i);
                                  }
                                );

                                removeError("goods_count");
                                setGoodsServiceRequest((prev) => ({
                                  ...prev,
                                  goodsServiceItemList:
                                    prev.goodsServiceItemList?.map((item) => {
                                      if (item.goodsId === goods.goodsId) {
                                        return {
                                          ...item,
                                          count: Number(value),
                                        };
                                      }
                                      return item;
                                    }),
                                }));
                              }}
                              error={errors["goods_" + index]}
                              ref={goodsRef.current[index]}
                            />
                          </div>
                        </div>
                        {goodsServiceItemList &&
                          goodsServiceItemList?.length - 1 !== index && (
                            <HorizontalRule
                              className="bg-brand-neutral-20"
                              margin="2.5"
                            />
                          )}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm ">
                      신청 가능한 요청 품목이 없습니다.
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <ErrorMessage error={errors["goods_count"]} />
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2.5">
            <CommonLabel label="기타 요청" />
            <RadioButtonGroup
              variantActive="select"
              className="w-full text-sm font-normal leading-[26px] tracking-[0.28px]"
              items={selectedExtraRequestItemOption}
              value={selectedExtraRequestItem}
              onChange={(selectedItem) => {
                removeError("extraRequest");
                setSelectedExtraRequestItem(selectedItem);
                setGoodsServiceRequest((prev) => ({
                  ...prev,
                  extraRequest: "",
                }));
              }}
            />
          </div>
          {selectedExtraRequestItem.key === "true" && (
            <div className="flex flex-col gap-2.5">
              <CommonLabel label="요청 사항" required />
              <CommonInput
                placeholder="요청 사항을 입력해주세요"
                maxLength={100}
                onChange={() => {
                  removeError("extraRequest");
                  setGoodsServiceRequest((prev) => ({
                    ...prev,
                    extraRequest: extraRequestRef.current?.value,
                  }));
                }}
                ref={extraRequestRef}
                value={goodsServiceRequest.extraRequest ?? ""}
                error={errors["extraRequest"]}
              />
            </div>
          )}
          <BottomButtonGroup
            title="접수하기"
            validation={!validation()}
            onClick={handleConfirm}
          />
        </div>
      </div>
      <CommonPopup
        title="상세 위치 선택"
        isOpen={popup === "requestType"}
        padding={0}
        unUseHorizonRule
        onClose={() => {
          if (goodsServiceRequest.requestType !== undefined) {
            if (goodsServiceRequest.requestType === "REFILL") {
              console.log(goodsServiceRequest);
              if (goodsServiceRequest.requestBuildingFloorId === undefined) {
                setPopup("requestTypeError");
                return;
              }
            }
            setPopup("");
            setSelectedRequestTypeItem(
              goodsServiceRequest.requestType === undefined
                ? selectedRequestTypeItemOption[0]
                : goodsServiceRequest.requestType === "REFILL"
                ? selectedRequestTypeItemOption[0]
                : selectedRequestTypeItemOption[1]
            );
          } else {
            setPopup("");
            //setPopup("requestTypeError");
          }
        }}
      >
        <div className="pb-11">
          <div className="flex flex-col gap-2.5 p-5">
            <div className="flex flex-col gap-2.5">
              <CommonLabel label="상세 장소" required />
              <RadioButtonGroup
                variantActive="select"
                className="w-full"
                items={selectedRequestTypeItemOption}
                value={selectedRequestTypeItem}
                onChange={(selectedItem) => {
                  removeError("selectFloor");
                  setSelectedRequestTypeItem(selectedItem);
                }}
              />
            </div>
            {selectedRequestTypeItem.key === "REFILL" && (
              <>
                <div className="flex flex-col gap-2.5">
                  <CommonLabel label="층수" required />

                  <CommonSelect
                    ref={selectFloorRef}
                    defaultValue={"층수를 선택해주세요."}
                    value={selectedFloor?.buildingFloorId.toString() ?? ""}
                    optionList={[
                      ...floorList
                        .sort((a, b) => {
                          return a.sortOrder < b.sortOrder ? -1 : 1;
                        })
                        .map((floor) => ({
                          value: floor.buildingFloorId,
                          label: floor.floorName,
                        })),
                    ]}
                    error={errors["selectFloor"]}
                    onChange={(floor) => {
                      removeError("selectFloor");
                      const selectedFloor = floorList.find(
                        (f) => f.buildingFloorId === floor.value
                      );
                      setSelectedFloor(selectedFloor);
                    }}
                    className="h-bottom-button"
                    optionOrder="between"
                  />
                </div>
                <div>
                  {Array.from({
                    length: floorList.length > 5 ? 5 : floorList.length,
                  }).map((item) => (
                    <div className="h-[50px]"></div>
                  ))}
                </div>
              </>
            )}
          </div>

          <BottomButtonGroup
            title="확인"
            validation={!validationRequestType()}
            onClick={handleConfirmRequestType}
          />
        </div>
      </CommonPopup>
      <CommonPopup
        title="접수 확인"
        isOpen={popup === "confirm"}
        padding={0}
        unUseHorizonRule
        onClose={() => {
          setPopup("");
        }}
      >
        <div className="px-5">
          <div className="pb-10">
            <div className="py-5 text-3xl font-light flex flex-row">
              <div className="font-medium">접수 정보</div>를 확인해주세요
            </div>
            <HorizontalRule margin="0" className="bg-black" />

            <div className="pt-[30px] text-lg font-medium">접수 내용</div>
            <HorizontalRule margin="0" />
            <div className="pb-[60px]">
              <div className="flex flex-col py-[6px]">
                <div className="flex flex-row gap-2.5">
                  <div className="flex flex-col text-brand-label-text text-sm min-w-[90px] py-[12px]">
                    접수 위치
                  </div>
                  <div className="flex flex-col py-[10px] gap-2">
                    {buildingInfo.buildingName}
                    <br />
                    {goodsServiceRequest.requestType === "REFILL"
                      ? `${selectedFloor?.floorName} OA실`
                      : "방문수령"}
                  </div>
                </div>
              </div>
              <HorizontalRule margin="0" />
              <div className="flex flex-col py-[6px]">
                <div className="flex flex-row gap-2.5">
                  <div className="flex flex-col text-brand-label-text text-sm min-w-[90px] py-[12px]">
                    접수 분류
                  </div>
                  <div className="flex flex-col py-[10px] gap-2">
                    <div className="border rounded-md py-1 px-3 cursor-pointer text-sm flex bg-[#F9DD7E] text-black font-normal w-max-content li-white">
                      <li>전용 서비스</li>
                    </div>
                    사무용품 신청
                  </div>
                </div>
              </div>
              <HorizontalRule margin="0" />
              <div className="flex flex-col py-[6px]">
                <div className="flex flex-row gap-2.5">
                  <div className="flex flex-col text-brand-label-text text-sm min-w-[90px] py-[12px]">
                    사용 부서
                  </div>
                  <div className="flex-col py-[10px] max-w-screen flex-wrap break-all">
                    {goodsServiceRequest.usedDepartment}
                  </div>
                </div>
              </div>
              <HorizontalRule margin="0" />
              <div className="flex flex-col py-[6px] w-full">
                <div className="flex flex-row gap-2.5">
                  <div className="flex flex-col text-brand-label-text text-sm min-w-[90px] py-[12px]">
                    상세 내용
                  </div>
                  <div className="flex-col py-[10px]">
                    {goodsServiceRequest.goodsServiceItemList?.map(
                      (goods, index) =>
                        goods.count > 0 && (
                          <div
                            key={goods.goodsId}
                            className="flex flex-row justify-between items-center"
                          >
                            {getGoodsName(goods.goodsId)} : {goods.count || 0}{" "}
                            개
                          </div>
                        )
                    )}
                    <br />
                    {selectedExtraRequestItem.key === "true" && (
                      <div className="flex-row flex-wrap break-all">
                        {goodsServiceRequest.extraRequest}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <BottomGroup zIndex="high">
            <div className="flex flex-row h-bottom-button">
              <CommonButton
                variant="grey"
                className="w-[100px] rounded-none"
                onClick={() => {
                  setPopup("");
                }}
              >
                수정
              </CommonButton>
              <CommonButton
                disabled={isLoading}
                className="w-full rounded-none"
                variant="primary"
                onClick={handleConfirmApply}
              >
                확인
              </CommonButton>
            </div>
          </BottomGroup>
        </div>
      </CommonPopup>
      <FindBuildingPopup
        popup={popup}
        onClose={() => {
          setPopup("");
        }}
        onChangeBuilding={(building) => {
          setBuildingInfo({
            buildingId: building.buildingId,
            buildingName: building.buildingName,
          });
          setGoodsServiceRequest((prev) => ({
            ...prev,
            requestType: "REFILL",
          }));
        }}
        type={"SPACE"}
        reason={reason}
      />
      <SimplePopup
        isOpen={popup === "success"}
        message={"신청이 완료되었습니다."}
        onClose={() => {
          setPopup("");
          navigate("/service/goods/confirmed", {
            state: {
              id: goodsServiceId,
            },
          });
        }}
      />
      <SimplePopup
        isOpen={popup === "requestTypeError"}
        message={"상세 위치를 선택해야 합니다."}
        onClose={() => setPopup("requestType")}
      />
      {simplePopupComponent}
    </>
  );
}
